import { useQuery, type QueryClient } from '@tanstack/react-query';
import type { DiscordFinish, DiscordStart, DiscordStatus } from '@teaplus/shared';
import { api } from './api';
import { useSession } from './queries';

export const DISCORD_KEY = ['discord'] as const;

/**
 * Whether this server offers Discord, the account's Discord (signing in) and the profile's own
 * Discord (its presence). profileId names a profile during setup, before one is chosen.
 */
export function useDiscordStatus(profileId?: string) {
  const active = useSession().data?.activeProfileId ?? null;
  return useQuery({
    queryKey: [...DISCORD_KEY, active, profileId ?? null],
    queryFn: () =>
      api<DiscordStatus>(
        `/api/auth/discord${profileId ? `?profileId=${encodeURIComponent(profileId)}` : ''}`,
      ),
  });
}

/** After connecting or disconnecting: every Discord status reads again. */
export const refreshDiscord = (qc: QueryClient) => qc.invalidateQueries({ queryKey: DISCORD_KEY });

export type DiscordIntent = 'login' | 'link' | 'profile';
const REDIRECT_KEY = 'tp:discord';
const POLL_MS = 2000;

interface Saved {
  state: string;
  token: string;
  intent: DiscordIntent;
  /** Where the tab goes once connected (the page that started it). */
  back?: string;
}

/**
 * Signs in with Discord, connects it to this account for signing in (link), or connects the
 * profile's own Discord (profile). In the Windows app Discord opens in the system browser while
 * this waits for it (resolves with the result, or null if cancelled). In a browser the tab goes to
 * Discord and comes back to /auth/discord, so this never resolves.
 */
export async function startDiscord(
  intent: DiscordIntent,
  opts: { remember?: boolean; signal?: AbortSignal; profileId?: string } = {},
): Promise<Exclude<DiscordFinish, { pending: true }> | null> {
  const desktop = !!window.teaplus?.desktop;
  const start = await api<DiscordStart>('/api/auth/discord/start', {
    body: {
      intent,
      mode: desktop ? 'poll' : 'redirect',
      remember: opts.remember ?? true,
      ...(opts.profileId ? { profileId: opts.profileId } : {}),
    },
  });
  if (!desktop) {
    try {
      sessionStorage.setItem(
        REDIRECT_KEY,
        JSON.stringify({
          state: start.state,
          token: start.token,
          intent,
          back: window.location.pathname,
        } satisfies Saved),
      );
    } catch {}
    window.location.assign(start.url);
    return new Promise(() => undefined);
  }
  // The app's window handler sends outside links to the system browser.
  window.open(start.url, '_blank', 'noopener');
  const body = { state: start.state, token: start.token };
  for (;;) {
    await new Promise((r) => setTimeout(r, POLL_MS));
    if (opts.signal?.aborted) return null;
    const r = await api<DiscordFinish>('/api/auth/discord/finish', { body });
    if (!r.pending) return r;
  }
}

/** Finishes a browser sign-in when Discord sends the tab back to /auth/discord. */
export async function finishDiscordRedirect(state: string) {
  let saved: Saved | null = null;
  try {
    saved = JSON.parse(sessionStorage.getItem(REDIRECT_KEY) ?? 'null') as Saved | null;
    sessionStorage.removeItem(REDIRECT_KEY);
  } catch {}
  if (!saved || saved.state !== state) return null;
  const r = await api<DiscordFinish>('/api/auth/discord/finish', {
    body: { state: saved.state, token: saved.token },
  });
  // Only a page of this app, never somewhere else.
  const back = saved.back && /^\/[\w/-]*$/.test(saved.back) ? saved.back : null;
  return r.pending ? null : { result: r, back };
}
